import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslate } from 'nucleus-text';
import PropTypes from 'prop-types';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { Row } from '@cvent/carina/components/Row';
import { Col } from '@cvent/carina/components/Col';
import { LoadingSpinner } from '@cvent/carina/components/LoadingSpinner';
import useTheme from '@cvent/blocks/hooks/useTheme';
import useBreakpoints from '@hooks/useBreakpoints';
import Button from '@cvent/carina/components/Button';
import { formatRelativeDate } from '@components/notifications/notificationFlyout/formatRelativeDate';
import { usePageActionsApi } from '@metrics/client/usePageActionsApi';
import { useInView } from 'react-intersection-observer';
import {
  NotificationLinkedContentPageType,
  isApptNotificationChannel,
  isInformationalApptNotification
} from '@components/notifications/utils';
import { ActionSource } from '@components/appointments/constants';
import { useAttendeeHubConfig } from '@hooks/useAttendeeHubConfig';
import Icon from '@components/shared/Icon';
import NotificationLinkedContent from '../NotificationLinkedContent';
import { notificationPropType } from '../notificationPropType';
import useTimezone from '../../../hooks/useTimezone';
import useLinkedContentVisiblity from '../useLinkedContentVisibility';

const useStyles = ({ theme, isMobile, hasConflict }) => {
  const containerPadding = '16px';

  return {
    separator: {},
    content: {
      flex: 1,
      width: !isMobile ? 305 : 198,
      paddingRight: !isMobile ? 16 : 8
    },
    title: {
      color: theme.font.color.base,
      fontSize: theme.font.base.size[4],
      fontWeight: theme.font.base.weight.bold,
      paddingBottom: -4
    },
    text: {
      color: theme.font.color.base,
      fontSize: theme.font.base.size[3],
      fontWeight: theme.font.base.weight.regular
    },
    readIndicator: {
      paddingBottom: 26,
      paddingRight: 8,
      flexBasis: 18,
      display: 'flex',
      alignItems: 'center'
    },
    webReadIndicator: {
      paddingBottom: 50
    },
    apptReadIndicator: {
      paddingBottom: 22
    },
    readDot: {
      width: 8,
      height: 8,
      borderRadius: theme.size.border.radius.fiveX,
      backgroundColor: theme.backgroundColor.danger.fill.base,
      fontSize: 0
    },
    contentContainer: {
      display: 'flex'
    },
    linkedContent: {
      fontSize: theme.font.base.size[1],
      display: 'flex',
      color: theme.font.color.soft,
      wordBreak: 'break-all',
      width: !isMobile ? 305 : 198,
      fontWeight: theme.font.base.weight.light,
      paddingTop: 4
    },
    timeStampContainer: {
      width: !isMobile ? 90 : 90,
      display: 'flex',
      flexDirection: 'column',
      marginRight: 2,
      fontSize: theme.font.base.size.xxs,
      color: theme.font.color.soft,
      textAlign: 'right'
    },
    notificationsContainer: {
      margin: 0,
      backgroundColor: theme.block.base,
      width: '100%'
    },
    nonLinkedContentContainer: {
      padding: !isMobile ? containerPadding : '16px 20px 16px 16px'
    },
    clickable: {
      cursor: 'pointer',
      backgroundColor: theme.block.base,
      textAlign: 'left',
      '&:hover': {
        backgroundColor: !isMobile ? theme.block.accent : null
      }
    },
    webUrlContainer: {
      padding: '16px 16px 10px 16px',
      display: 'block',
      textDecoration: 'none'
    },
    actionContentContainer: {
      paddingLeft: 20,
      paddingTop: 16,
      paddingBottom: 11,
      paddingRight: 5
    },
    conflictIconContainer: {
      display: 'inline-block',
      float: 'right',
      position: 'relative',
      bottom: 20
    },
    conflictIcon: {
      width: '50%',
      margin: '0 auto'
    },
    conflictText: {
      color: theme.backgroundColor.danger.fill.base
    },
    icon: {
      color: theme.font.color.soft
    },
    buttonDiv: {
      display: 'inline-block',
      button: {
        display: 'inline-block'
      },
      'button + button': { marginLeft: 16 }
    },
    apptTimeStampContainer: {
      width: !isMobile ? 90 : 76,
      marginRight: 0,
      paddingTop: 2,
      fontSize: theme.font.base.size.xxs,
      color: theme.font.color.soft,
      textAlign: 'right'
    },
    borderStyle: {
      border: 'none',
      borderBottom: 'solid',
      borderBottomWidth: theme.size.border.base,
      borderBottomColor: theme.borderColor.soft
    },
    apptNotificationsContainer: {
      margin: 0,
      position: 'relative',
      paddingBottom: 0,
      display: 'inline-block',
      verticalAlign: 'top'
    },
    apptActionLoader: {
      paddingTop: 15
    },
    apptStatusText: {
      verticalAlign: 'super',
      paddingLeft: 5,
      fontSize: theme.font.base.size.xxs,
      color: theme.font.color.success.base
    },
    apptStatusContainer: {
      paddingTop: 20,
      paddingLeft: !isMobile ? 25 : 20
    },
    apptStatus: {
      display: 'inline-block'
    },
    scheduleLink: {
      display: 'inline-block',
      position: 'relative',
      float: hasConflict ? 'left' : 'right',
      color: theme.font.color.interactive.base,
      fontSize: theme.font.base.size.m,
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    hRule: {
      height: 1,
      border: 'none',
      backgroundColor: theme.borderColor.soft,
      justifyContent: 'center',
      marginLeft: 34,
      marginRight: 18
    },
    conflictedMessage: {
      paddingLeft: 20,
      paddingTop: 8,
      color: theme.font.color.base,
      fontSize: theme.font.base.size[3],
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'left'
    },
    conflictedText: {
      lineHeight: '130%',
      paddingLeft: '4px'
    }
  };
};

const AppointmentActionContent = ({
  testID,
  recipientStatus,
  canAccept,
  canDecline,
  hasConflict,
  acceptMutation,
  declineMutation,
  isAccepted,
  isDeclined,
  apptId
}) => {
  const theme = useTheme();
  const { font } = theme;
  const { isMobile } = useBreakpoints();
  const styles = useStyles({ theme, isMobile, hasConflict });
  const { translate } = useTranslate();

  let icon;
  let color;
  let status;
  let viewScheduleLink = null;
  if (hasConflict || isAccepted) {
    viewScheduleLink = (
      <Button
        testID={`${testID}-viewSchedule`}
        text={translate('AppointmentNotificationPanel-ViewScheduleLink')}
        size={'s'}
      />
    );
  }

  if (isAccepted) {
    color = font.color.success.base;
    icon = <Icon name="check-circle" size="lg" color={color} aria-hidden />;
    status = translate('AppointmentNotificationPanel-AcceptedText');
  } else if (isDeclined) {
    color = font.color.danger.base;
    icon = <Icon name="x-circle" size="lg" color={color} aria-hidden />;
    status = translate('AppointmentNotificationPanel-DeclinedText');
  }

  if (isAccepted || isDeclined) {
    return (
      <div css={styles.apptStatusContainer}>
        <div css={styles.apptStatus}>
          {icon}
          <span css={{ ...styles.apptStatusText, color }}>{status}</span>
        </div>
        {viewScheduleLink}
      </div>
    );
  }

  return (
    <div css={styles.actionContentContainer}>
      {recipientStatus?.toUpperCase() !== 'CONFIRMED' && (
        <div css={styles.buttonDiv}>
          {canAccept && !hasConflict && (
            <Button
              testID={`${testID}-appt-accept-btn`}
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                acceptMutation({
                  variables: {
                    id: apptId,
                    source: ActionSource.ATTENDEE_HUB_WEB_NOTIFICATION
                  }
                });
              }}
              text={translate('AppointmentPanel-Button-Accept')}
              appearance="lined"
              size={'s'}
            />
          )}
          {canDecline && !hasConflict && (
            <Button
              testID={`${testID}-appt-decline-btn`}
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                declineMutation({
                  variables: {
                    params: {
                      id: apptId,
                      comment: ''
                    },
                    source: ActionSource.ATTENDEE_HUB_WEB_NOTIFICATION
                  }
                });
              }}
              text={translate('AppointmentPanel-Button-Decline')}
              appearance="ghost"
              size={'s'}
            />
          )}
        </div>
      )}
      {viewScheduleLink}
    </div>
  );
};

AppointmentActionContent.propTypes = {
  testID: PropTypes.string,
  surveyUrl: PropTypes.string,
  recipientStatus: PropTypes.string,
  canAccept: PropTypes.bool,
  canDecline: PropTypes.bool,
  hasConflict: PropTypes.bool,
  acceptMutation: PropTypes.func,
  declineMutation: PropTypes.func,
  isAccepted: PropTypes.bool,
  isDeclined: PropTypes.bool,
  apptId: PropTypes.string
};

/**
 * Component to display notification content
 */
const NotificationContent = ({
  notification,
  testID,
  onClose,
  eventId,
  markAndPublishNotificationsAsRead,
  containerRef,
  onNotificationVisible,
  surveyUrl,
  appointment,
  setSize,
  index
}) => {
  const theme = useTheme();
  const { isMobile } = useBreakpoints();
  const {
    acceptMutation,
    isAccepted,
    declineMutation,
    isDeclined,
    isAccepting,
    isDeclining
  } = appointment;
  const { date } = useTimezone();
  const { translate } = useTranslate();
  const notificationRef = useRef(null);
  const notificationText = notification.body;
  const notificationTitle = notification.title;
  const { linkedContent } = notification;
  const isRead = !!notification.isRead;
  const isApptNotification = isApptNotificationChannel(
    notification.channelName
  );
  const isInfoApptNotification = isInformationalApptNotification(notification);
  const { canAccept, canDecline, hasConflict, recipientStatus } =
    notification.metadata?.entity || {};
  const { ref, entry, inView } = useInView({
    root: containerRef?.current || null,
    threshold: 0.2, // choosing this value so that notification with very large body gets marked as read.
    trackVisibility: true,
    delay: 100
  });
  useEffect(() => {
    const { id } = notification;
    if (
      (entry?.isVisible || inView) &&
      !isRead &&
      (!linkedContent || !containsLinkedContent)
    ) {
      onNotificationVisible(id);
    }
  }, [
    entry,
    notification,
    inView,
    isRead,
    linkedContent,
    onNotificationVisible
  ]);

  useEffect(() => {
    if (ref?.current) {
      setSize(index, ref.current.clientHeight);
    }
  }, [ref, index, setSize]);

  const setRefs = useCallback(
    node => {
      // Ref's from useRef needs to have the node assigned to `current`
      ref.current = node;
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      ref(node);
    },
    [ref]
  );

  useEffect(() => {
    const { onReadOnlyApptNotificationVisible = () => null } = notification;
    onReadOnlyApptNotificationVisible(entry, inView, onNotificationVisible);
  }, [entry, inView, notification, onNotificationVisible]);

  const timeStamp = useMemo(() => {
    const now = new Date();
    return formatRelativeDate(
      notification.publishedAt,
      now,
      date,
      translate,
      false
    );
  }, [notification.publishedAt, date, translate]);

  const { customPageSettings } = useAttendeeHubConfig();
  const { isLinkedContentVisible } = useLinkedContentVisiblity();
  const containsLinkedContent =
    linkedContent != null &&
    isLinkedContentVisible(
      linkedContent.content?.type,
      linkedContent.content?.id
    );
  const isWebUrl = containsLinkedContent
    ? linkedContent.contentType === 'WebUrl'
    : false;
  const isDocumentOrExternalPage =
    containsLinkedContent &&
    (notification.linkedContent.content.type ===
      NotificationLinkedContentPageType.DOCUMENT_PAGES ||
      notification.linkedContent.content.type ===
        NotificationLinkedContentPageType.EXTERNAL_PAGES);
  const { href, name } = isWebUrl ? linkedContent.content : {};
  const linkedContentText = isWebUrl ? name || encodeURI(href) : null;
  const { notificationClicked } = usePageActionsApi();

  let documentOrExternalPageTitle = '';
  if (isDocumentOrExternalPage) {
    const customPage = customPageSettings.find(
      e => e.pageId === notification.linkedContent.content.id
    );
    if (customPage) {
      documentOrExternalPageTitle = customPage.pageName;
    }
  }

  const styles = useStyles({
    theme,
    isMobile,
    isWebUrl,
    isDocumentOrExternalPage
  });

  const onLinkedNotificationClick = useCallback(() => {
    onClose();
    notificationClicked({
      notificationId: notification.id,
      hasLinkedContent: true,
      contentType:
        linkedContent.contentType === 'LinkedExternalPage'
          ? 'LinkedPage'
          : linkedContent.contentType,
      content: linkedContent.content,
      isClickedFromPush: false
    });
    if (!isRead) {
      markAndPublishNotificationsAsRead([notification.id]);
    }
  }, [
    notificationClicked,
    markAndPublishNotificationsAsRead,
    onClose,
    isRead,
    linkedContent,
    notification
  ]);

  const apptId = notification.metadata?.entity?.entityId;
  const notificationContentContainer = useMemo(
    () => (
      <Col
        padding={{
          top: 0,
          start: 0,
          end: 0,
          bottom: 0
        }}
      >
        <div
          css={[styles.contentContainer]}
          {...injectTestId(`${testID}-notification-container`)}
        >
          <div
            css={[
              styles.readIndicator,
              (isWebUrl || isDocumentOrExternalPage) && styles.webReadIndicator,
              isApptNotification && styles.apptReadIndicator
            ]}
          >
            {!isRead && (
              <div
                css={styles.readDot}
                {...injectTestId(`${testID}-unread-badge`)}
              >
                {translate('Unread-Notifications-Aria')}
              </div>
            )}
          </div>
          <div css={styles.content}>
            <div
              css={styles.title}
              {...injectTestId(`${testID}-notification-title`)}
            >
              {notificationTitle}
            </div>
            <div
              css={styles.text}
              {...injectTestId(`${testID}-notification-text`)}
            >
              {notificationText}
            </div>
            {isWebUrl || isDocumentOrExternalPage ? (
              <div
                padding={{
                  top: 0,
                  start: 0,
                  end: 0,
                  bottom: 0
                }}
              >
                <div
                  css={styles.linkedContent}
                  {...injectTestId(`${testID}-WebUrl`)}
                >
                  <div>
                    <Icon
                      name="external-link"
                      size="xs"
                      color={theme.font.color.soft}
                      aria-hidden
                    />
                  </div>
                  <div css={{ marginLeft: !isMobile ? 8 : 4 }}>
                    {isWebUrl ? linkedContentText : documentOrExternalPageTitle}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <div
            css={styles.timeStampContainer}
            {...injectTestId(`${testID}-notification-timestamp`)}
          >
            <div
              css={{
                paddingBottom: isWebUrl || isDocumentOrExternalPage ? 24 : null
              }}
            >
              {timeStamp}
            </div>
            <div
              css={{
                alignSelf: 'end',
                paddingTop: 4
              }}
            >
              {containsLinkedContent && !isApptNotification && (
                <Icon
                  name="chevron-right"
                  size="md"
                  aria-hidden
                  {...injectTestId(`${testID}-chevron`)}
                />
              )}
            </div>
          </div>
        </div>
        {isApptNotification && !isInfoApptNotification && (
          <div>
            {hasConflict && (
              <div
                css={styles.conflictedMessage}
                {...injectTestId(`${testID}-appt-conflict-info`)}
              >
                <div>
                  <Icon
                    name="alert-circle"
                    size="xs"
                    color={theme.backgroundColor.danger.fill.base}
                    aria-hidden
                  />
                </div>
                <div css={styles.conflictedText}>
                  {translate('AppointmentNotificationPanel-Conflict-Text')}
                </div>
              </div>
            )}
            <AppointmentActionContent
              testID={testID}
              acceptMutation={acceptMutation}
              declineMutation={declineMutation}
              isAccepted={isAccepted}
              isDeclined={isDeclined}
              recipientStatus={recipientStatus}
              canAccept={canAccept}
              canDecline={canDecline}
              hasConflict={hasConflict}
              apptId={apptId}
            />
          </div>
        )}
      </Col>
    ),
    [
      notificationText,
      notificationTitle,
      timeStamp,
      styles,
      isRead,
      testID,
      translate,
      isApptNotification,
      canAccept,
      canDecline,
      hasConflict,
      recipientStatus,
      acceptMutation,
      declineMutation,
      isAccepted,
      isDeclined,
      isInfoApptNotification,
      apptId
    ]
  );

  if (isApptNotification && (isAccepting || isDeclining)) {
    return (
      <div css={styles.apptActionLoader}>
        <LoadingSpinner
          testID={`${testID}-action-loading`}
          aria-live="polite"
          aria-label={translate(
            'AppointmentNotificationPanel-ActionInProgress'
          )}
        />
      </div>
    );
  }

  return (
    <div
      {...injectTestId(`${testID}-list-item`)}
      id={notification.id}
      ref={setRefs}
    >
      {containsLinkedContent && !isDeclined ? (
        <div>
          <Row margin={0}>
            <Col padding={0}>
              <div
                css={[
                  styles.notificationsContainer,
                  !isApptNotification && styles.clickable
                ]}
                {...injectTestId(`${testID}-container-containsLinkedContent`)}
                ref={notificationRef}
              >
                <NotificationLinkedContent
                  notification={notification}
                  styles={[styles.webUrlContainer]}
                  testID={testID}
                  onClick={() => {
                    if (notification.onClick) {
                      notification.onClick(
                        onClose,
                        notificationClicked,
                        markAndPublishNotificationsAsRead
                      );
                    } else {
                      onLinkedNotificationClick();
                    }
                  }}
                  eventId={eventId}
                  surveyUrl={surveyUrl}
                >
                  {notificationContentContainer}
                </NotificationLinkedContent>
              </div>
            </Col>
          </Row>
        </div>
      ) : (
        <div
          css={[
            styles.notificationsContainer,
            styles.nonLinkedContentContainer
          ]}
          {...injectTestId(`${testID}-container-noLinkedContent`)}
        >
          {notificationContentContainer}
        </div>
      )}
      {<div css={styles.hRule} />}
    </div>
  );
};

NotificationContent.propTypes = {
  testID: PropTypes.string,
  onClose: PropTypes.func,
  markAndPublishNotificationsAsRead: PropTypes.func,
  onNotificationVisible: PropTypes.func,
  notification: notificationPropType,
  eventId: PropTypes.string,
  containerRef: PropTypes.object,
  surveyUrl: PropTypes.string,
  appointment: PropTypes.shape({
    acceptMutation: PropTypes.func,
    declineMutation: PropTypes.func,
    isAccepted: PropTypes.bool,
    isDeclined: PropTypes.bool,
    isAccepting: PropTypes.bool,
    isDeclining: PropTypes.bool
  }),
  setSize: PropTypes.func,
  index: PropTypes.number
};

NotificationContent.defaultProps = {
  testID: 'notification-content',
  containerRef: {}
};

export default NotificationContent;
